/**
 * ============================================================================
 * NotificationInbox — Phase 3 frontend
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: read-only inbox of in-app notifications + channel preferences pane.
 *          Reads from GET /api/v1/notifications/inbox; preferences from
 *          GET /api/v1/notifications/preferences.
 *
 * UX RULES:
 *   - IN_APP channel cannot be opted out (the toggle is disabled with a
 *     tooltip explaining why — regulatory requirement for signature reminders).
 *   - Unread notifications display a bold marker; clicking marks READ via
 *     POST /api/v1/notifications/{id}/read.
 *   - Body text is rendered VERBATIM as resolved by the server in the
 *     recipient's CURRENT locale.
 *
 * URS rows: NOTIF-001..010, I18N-001/002.
 * ============================================================================
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import {
  useNotificationInbox,
  useNotificationPreferences,
  useMarkNotificationRead,
  useUpdateNotificationPreferences,
} from '../../api/hooks';
import { AiAdvisoryChip } from '../../components/AiAdvisoryChip';
import styles from './NotificationInbox.module.css';

export function NotificationInbox() {
  const { t } = useTranslation();
  const inboxQ = useNotificationInbox();
  const prefsQ = useNotificationPreferences();
  const markReadMut = useMarkNotificationRead();
  const updatePrefsMut = useUpdateNotificationPreferences();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const items = useMemo(() => {
    const all = inboxQ.data?.items ?? [];
    return filter === 'unread' ? all.filter((n) => n.state !== 'READ') : all;
  }, [inboxQ.data, filter]);

  // AI-104 — Daily Digest prompt is grounded in counts so the model summarizes
  // the recipient's actual workload rather than producing generic text.
  const allItems = inboxQ.data?.items ?? [];
  const unreadCount = allItems.filter((n) => n.state !== 'READ').length;
  const urgentSubset = allItems.slice(0, 5).map((n) => ({
    id: n.id, kind: n.template_key, text: (n.body_text || '').slice(0, 120),
  }));

  return (
    <div className={styles.inboxScreen}>
      <PageHero
        eyebrow={t('inbox.eyebrow', { defaultValue: 'Inbox' })}
        title={t('inbox.title', { defaultValue: 'Notifications' })}
        subtitle={t('inbox.subtitle', {
          defaultValue: 'In-app messages and signature reminders.',
        })}
      />

      {/* AI-104 — Daily Digest */}
      {allItems.length > 0 && (
        <section className={styles.digestRow} aria-label="Daily digest">
          <AiAdvisoryChip
            surface="dashboard.daily-digest"
            prompt={
              `Spectropharma daily inbox digest. Total notifications: ${allItems.length}. ` +
              `Unread: ${unreadCount}. Top 5 templates: ${JSON.stringify(urgentSubset)}. ` +
              `Write a one-sentence digest of what the reader should attend to today, then a 2-3 bullet ` +
              `'today's priorities' list. Quote template keys where useful. Return JSON {summary, concerns:[]}.`
            }
            cacheKey={`daily-digest:${new Date().toISOString().slice(0, 10)}:${allItems.length}:${unreadCount}`}
            jsonSchema={{
              type: 'object',
              properties: {
                summary:  { type: 'string' },
                concerns: { type: 'array', items: { type: 'string' } },
              },
              required: ['summary'],
            }}
            citations={urgentSubset.slice(0, 3).map((u) => ({
              kind: 'notification', id: u.id, label: u.kind,
            }))}
          />
        </section>
      )}

      <div className={styles.layout}>
        <Card>
          <CardHeader>
            <div className={styles.inboxHeader}>
              <h2 className={styles.sectionTitle}>
                {t('inbox.heading', { defaultValue: 'Inbox' })}{' '}
                <span className={styles.count}>({items.length})</span>
              </h2>
              <div className={styles.filterControls}>
                <Button
                  size="sm"
                  variant={filter === 'all' ? 'primary' : 'tertiary'}
                  onClick={() => setFilter('all')}
                >
                  {t('inbox.filter.all', { defaultValue: 'All' })}
                </Button>
                <Button
                  size="sm"
                  variant={filter === 'unread' ? 'primary' : 'tertiary'}
                  onClick={() => setFilter('unread')}
                >
                  {t('inbox.filter.unread', { defaultValue: 'Unread' })}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {inboxQ.isLoading && <p role="status">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
            {inboxQ.isError && (
              <p role="alert" className={styles.error}>
                {t('inbox.errors.loadFailed', { defaultValue: 'Could not load notifications.' })}
              </p>
            )}
            {items.length === 0 && !inboxQ.isLoading && (
              <p className={styles.empty}>
                {t('inbox.empty', { defaultValue: 'No notifications.' })}
              </p>
            )}
            <ul className={styles.list}>
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`${styles.item} ${n.state === 'READ' ? styles.read : styles.unread}`}
                >
                  <div className={styles.itemBody}>
                    <p className={styles.itemText}>{n.body_text || n.template_key}</p>
                    <span className={styles.itemMeta}>
                      {n.channel} · {n.sent_at_utc ?? n.created_at_utc}
                    </span>
                  </div>
                  {n.state !== 'READ' && (
                    <Button
                      size="sm"
                      variant="tertiary"
                      onClick={() => markReadMut.mutate({ id: n.id })}
                      aria-label={t('inbox.actions.markRead', { defaultValue: 'Mark read' })}
                    >
                      {t('inbox.actions.markRead', { defaultValue: 'Mark read' })}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className={styles.sectionTitle}>
              {t('inbox.preferences.title', { defaultValue: 'Channel preferences' })}
            </h2>
          </CardHeader>
          <CardBody>
            {prefsQ.isLoading && <p role="status">Loading…</p>}
            {prefsQ.data && (
              <div className={styles.prefsForm}>
                <PreferenceToggle
                  label={t('inbox.preferences.email', { defaultValue: 'Email' })}
                  channel="EMAIL"
                  enabled={prefsQ.data.EMAIL?.enabled ?? true}
                  disabled={false}
                  hint={t('inbox.preferences.emailHint', { defaultValue: 'Receive workflow updates by email.' })}
                  onChange={(enabled) =>
                    updatePrefsMut.mutate({ channel: 'EMAIL', enabled })
                  }
                />
                <PreferenceToggle
                  label={t('inbox.preferences.inApp', { defaultValue: 'In-app (mandatory)' })}
                  channel="IN_APP"
                  enabled={true}
                  disabled={true}
                  hint={t('inbox.preferences.inAppHint', {
                    defaultValue: 'Cannot be disabled — signature reminders are regulatory.',
                  })}
                  onChange={() => undefined}
                />
                <PreferenceToggle
                  label={t('inbox.preferences.sms', { defaultValue: 'SMS' })}
                  channel="SMS"
                  enabled={prefsQ.data.SMS?.enabled ?? false}
                  disabled={false}
                  hint={t('inbox.preferences.smsHint', { defaultValue: 'For urgent operational alerts only.' })}
                  onChange={(enabled) =>
                    updatePrefsMut.mutate({ channel: 'SMS', enabled })
                  }
                />
                <p className={styles.quietHours}>
                  {t('inbox.preferences.quietHoursLabel', { defaultValue: 'Quiet hours (local):' })}{' '}
                  <strong>{prefsQ.data.quiet_hours ?? '—'}</strong>
                </p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

interface PreferenceToggleProps {
  label: string;
  channel: 'EMAIL' | 'IN_APP' | 'SMS';
  enabled: boolean;
  disabled: boolean;
  hint: string;
  onChange: (enabled: boolean) => void;
}

function PreferenceToggle({ label, enabled, disabled, hint, onChange }: PreferenceToggleProps) {
  return (
    <label className={styles.toggleRow}>
      <input
        type="checkbox"
        checked={enabled}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-describedby={`${label}-hint`}
      />
      <span className={styles.toggleLabel}>{label}</span>
      <small id={`${label}-hint`} className={styles.toggleHint}>
        {hint}
      </small>
    </label>
  );
}
